/** Mirrors ImportUpparbetadeKostnaderResultDTO from ProjektApi. */
export interface UpparbetadeKostnaderImportResult {
  /** 'SEK' | 'TKR' | 'MSEK' as reported by the extraction. */
  unit: UpparbetadeKostnaderImportUnit;
  /** The open (not yet reported) years the import may fill; mirrors the schema sent to the backend. */
  years: number[];
  projektparter: UpparbetadeKostnaderImportProjektpart[];
  cells: UpparbetadeKostnaderImportCell[];
  unmatchedBlocks: string[];
  /** Workbook-wide issues only; per-part issues are on each projektpart. */
  warnings: string[];
  /** Absent when the backend has not preprocessed the workbook (older API). */
  preprocessing?: UpparbetadeKostnaderImportPreprocessing;
  /** Rows the preprocessing left out of the sums. */
  excluded?: UpparbetadeKostnaderImportExcludedEntry[];
  /** Server-side wall-clock per phase; absent from older backends. */
  timings?: UpparbetadeKostnaderImportTimings;
}

export interface UpparbetadeKostnaderImportTimings {
  /** Structure detection + filtering; 0 when preprocessing was skipped. */
  preprocessingMs: number;
  extractionMs: number;
  /** Whole request handling, upload excluded. */
  totalMs: number;
}

export type UpparbetadeKostnaderImportUnit = 'SEK' | 'TKR' | 'MSEK';

export type UpparbetadeKostnaderImportLayout =
  'ledger' | 'matrix' | 'summary' | 'unknown';

export type UpparbetadeKostnaderImportDateGranularity =
  'day' | 'week' | 'month' | 'quarter' | 'year';

/** Mirrors ImportPreprocessingDTO: what the backend's structure detection concluded before filtering by period. */
export interface UpparbetadeKostnaderImportPreprocessing {
  layout: UpparbetadeKostnaderImportLayout;
  /** Header names the row date was derived from, e.g. ["År", "Månad"]. */
  dateColumns?: string[];
  dateGranularity?: UpparbetadeKostnaderImportDateGranularity;
  sheetsAnalyzed: string[];
  sheetsSkipped: string[];
  rowsTotal: number;
  rowsInPeriod: number;
  rowsExcluded: number;
  /** 0..1 for the structure detection itself. */
  confidence?: number;
}

/** Sums in file units (before unit scaling) so the user can check that what was kept adds up. */
export interface UpparbetadeKostnaderImportReconciliation {
  totalInFile: number;
  totalInPeriod: number;
  totalExcludedOutsidePeriod: number;
  totalUnmatchedCategory: number;
}

export type UpparbetadeKostnaderImportExcludedReason =
  | 'outsidePeriod'
  | 'unmatchedCategory'
  /** Row spans a period boundary (e.g. a whole month when the period starts mid-month); kept but flagged. */
  | 'partialOverlap';

export interface UpparbetadeKostnaderImportExcludedEntry {
  /** Absent when the row could not be attributed to a part. */
  projektpartId?: string;
  sourceSheet: string;
  sourceRange: string;
  /** ISO date or period label as it appeared in the file. */
  date?: string;
  label?: string;
  /** Amount as written in the sheet, before unit scaling. */
  summa: number;
  reason: UpparbetadeKostnaderImportExcludedReason;
}

/** Mirrors the reasoning.effort values accepted by ProjektApi; undefined uses the server default. */
export type UpparbetadeKostnaderImportReasoningEffort =
  'none' | 'minimal' | 'low' | 'medium' | 'high';

export const UPPARBETADE_KOSTNADER_IMPORT_UNIT_FACTOR: Record<
  UpparbetadeKostnaderImportUnit,
  number
> = {
  SEK: 1,
  TKR: 1000,
  MSEK: 1_000_000,
};

export interface UpparbetadeKostnaderImportProjektpart {
  id: string;
  koordinerande: boolean;
  namn?: string;
  organisationsnummer?: string;
  adress?: string;
  /** 0..1 for the part's imported values as a whole; only present in results, never in the schema. */
  confidence?: number;
  /** Why the confidence is below 0.8, as reported by the extraction. */
  confidenceReason?: string;
  /** Things to double-check for this part; only present in results. */
  warnings?: string[];
  /** Only present in results from a preprocessing backend. */
  reconciliation?: UpparbetadeKostnaderImportReconciliation;
}

/** Mirrors UpparbetadeKostnaderSlotSchema in ProjektApi: the closed set of parter and open redovisningsperioder the extraction may fill. */
export interface UpparbetadeKostnaderSlotSchema {
  perioder: UpparbetadeKostnaderSlotPeriod[];
  projektparter: UpparbetadeKostnaderImportProjektpart[];
}

/**
 * One open (not yet reported) redovisningsperiod. Workbooks are often broken down by month or by transaction
 * date rather than by year, so the exact boundaries (not just the year) are needed to sum correctly —
 * e.g. a period starting mid-year must exclude transactions from earlier in that same calendar year.
 */
export interface UpparbetadeKostnaderSlotPeriod {
  /** Calendar year the period's costs are booked to (matches the stored `ar` bucket). */
  ar: number;
  /** ISO date (yyyy-MM-dd), inclusive. */
  startDate: string;
  /** ISO date (yyyy-MM-dd), inclusive. */
  endDate: string;
}

export interface UpparbetadeKostnaderImportCell {
  projektpartId: string;
  /** Suggested kostnadsslag 1-5 (matches Kostnadstyp); absent when the analysis had no suggestion. */
  rowType?: number;
  ar: number;
  /** Amount of the single source cell as written in the sheet, before unit scaling. */
  summa: number;
  sourceSheet?: string;
  sourceCell?: string;
  /** Text of the source row, read from the file. */
  label?: string;
  /** Same for every period of one source row. */
  rowKey?: string;
}

/** What the user confirmed in the preview; already scaled to SEK. */
export interface UpparbetadeKostnaderImportApplyResult {
  projektparter: UpparbetadeKostnaderImportApplyProjektpart[];
}

export interface UpparbetadeKostnaderImportApplyProjektpart {
  id: string;
  /** kostnadsslag id ('1'..'5') -> year -> SEK */
  kostnader: Record<string, Record<number, number>>;
}
